import type { Getter, Setter } from 'jotai';
import { getRandomId } from '../../../../../common/utils/utils';
import type { UndoRedoActionCommand } from '../../../../../modernization-common/undo-redo/types';
import { ANNOTATION_TYPE } from '../../../../labelling/constants';
import { getMergedPolygonDataPoints, transformRectanglesToPolygons } from '../../../../labelling/reducers/actionReducers/multi-select/helpers';
import type { AnnotationT, PolygonAnnotationT } from '../../../../labelling/types';
import { buildAnnotationForOutput } from '../../../utils/build-annotation-for-output';
import { annotationLabelsAtom, annotationMetadataAtom, loadedAnnotationFramesAtom } from '../../annotation-atoms';
import { STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
import { selectedAnnotationIdsAtom } from '../../tool-atoms';
import { currentFrameAtom } from '../../video-playback-atoms';
import type { MergePolygonEventPayload } from '../events';
export const mergePolygonsCommandFactory = (get: Getter, set: Setter): UndoRedoActionCommand<MergePolygonEventPayload, void> => {
  let deletedAnnotations: Record<string, AnnotationT> = {};
  let mergedAnnotationId = '';
  let previousSelectedIds: string[] = [];
  let selectedFrameIndex = -1;
  return {
    execute: () => {
      const currentFrame = get(currentFrameAtom);
      const selectedIds = get(selectedAnnotationIdsAtom);
      const frameMap = get(loadedAnnotationFramesAtom).get(currentFrame);
      if (!frameMap) {
        return;
      }
      const metadata = get(annotationMetadataAtom);
      const annotationLabelsList = get(annotationLabelsAtom);
      selectedFrameIndex = currentFrame;
      previousSelectedIds = selectedIds;
      deletedAnnotations = {};
      mergedAnnotationId = mergedAnnotationId || getRandomId();
      const annotations: AnnotationT[] = [];
      for (const id of selectedIds) {
        const framePayload = frameMap.get(id);
        const meta = metadata.get(id);
        if (!framePayload || !meta) {
          continue;
        }
        const ann = buildAnnotationForOutput(framePayload, meta, annotationLabelsList, currentFrame);
        annotations.push(ann);
        deletedAnnotations[id] = ann;
      }
      if (annotations.length < 2) {
        return;
      }
      const annotationsCopy: AnnotationT[] = annotations.map(a => ({
        ...a
      }));
      transformRectanglesToPolygons(annotationsCopy);
      const polygons = (annotationsCopy as PolygonAnnotationT[]);
      const mergedPoints = getMergedPolygonDataPoints(polygons);
      const firstAnnotation = annotations[0];
      if (!firstAnnotation) {
        return;
      }
      const mergedPolygon: PolygonAnnotationT = {
        annotationType: ANNOTATION_TYPE.POLYGON,
        annotationLabel: firstAnnotation.annotationLabel,
        data: {
          points: [...mergedPoints]
        }
      };
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.WRITE_ANNOTATION,
        frameIndex: selectedFrameIndex,
        annotationId: mergedAnnotationId,
        annotation: mergedPolygon
      });
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.BULK_DELETE,
        deletes: Object.keys(deletedAnnotations).map(id => ({
          frameIndex: selectedFrameIndex,
          annotationId: id
        }))
      });
      set(selectedAnnotationIdsAtom, [mergedAnnotationId]);
    },
    undo: () => {
      if (!mergedAnnotationId) {
        return;
      }
      if (selectedFrameIndex < 0) {
        return;
      }
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.DELETE_ANNOTATION,
        frameIndex: selectedFrameIndex,
        annotationId: mergedAnnotationId
      });
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.BULK_WRITE,
        writes: Object.entries(deletedAnnotations).map(([id, annotation]) => ({
          frameIndex: selectedFrameIndex,
          annotationId: id,
          annotation
        }))
      });
      set(selectedAnnotationIdsAtom, previousSelectedIds);
    }
  };
};